import { LinkOutlined } from '@ant-design/icons';
import { SharedSpinner, SupplierSelect } from '@components';
import { InvoiceFileUpload } from '../../InventoryTable/components/InvoiceFileUpload';
import { L } from '@lib';
import { ParsedInvoice, PurchaseOrderMatchResult, SupplierLookup } from '@services';
import { Button, Drawer, Space, Typography } from 'antd';
import React from 'react';
import { POReceiveLineCard } from './POReceiveLineCard';

const { Text } = Typography;

interface IProps {
  open: boolean;
  uploadedFile: { base64: string; name: string; contentType: string } | null;
  parsing: boolean;
  matching: boolean;
  submitting: boolean;
  captureToken: string | null;
  parsedInvoice: ParsedInvoice | null;
  supplierId: string | undefined;
  attachedSupplier: SupplierLookup | undefined;
  matchResults: PurchaseOrderMatchResult[];
  lineQtys: Record<number, number>;
  lineCosts: Record<number, number | undefined>;
  lineSelectedPO: Record<number, string | undefined>;
  onClose: () => void;
  onFileSelect: (file: File) => void;
  onRemoveFile: () => void;
  onParse: () => void;
  onAttachSupplier: () => void;
  onSupplierChange: (id: string | undefined) => void;
  onMatch: () => void;
  onLineQtyChange: (index: number, val: number) => void;
  onLineCostChange: (index: number, val: number | undefined) => void;
  onLineSelectPO: (index: number, id: string) => void;
  onSubmit: () => void;
}

export const POInvoiceReceiveDrawerPresentation: React.FC<IProps> = ({
  open,
  uploadedFile,
  parsing,
  matching,
  submitting,
  captureToken,
  parsedInvoice,
  supplierId,
  attachedSupplier,
  matchResults,
  lineQtys,
  lineCosts,
  lineSelectedPO,
  onClose,
  onFileSelect,
  onRemoveFile,
  onParse,
  onAttachSupplier,
  onSupplierChange,
  onMatch,
  onLineQtyChange,
  onLineCostChange,
  onLineSelectPO,
  onSubmit,
}) => {
  const hasResults = matchResults.length > 0;
  const matchedCount = matchResults.filter((r) => r.matchStatus !== 'NoMatch').length;
  const canSubmit = hasResults && matchedCount > 0 && !!supplierId;

  return (
    <Drawer
      title={L('Receive from invoice')}
      open={open}
      onClose={onClose}
      width={540}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>{L('Cancel')}</Button>
          {hasResults && (
            <Button type="primary" loading={submitting} disabled={!canSubmit} onClick={onSubmit}>
              {L('Receive')} {matchedCount > 0 ? `(${matchedCount})` : ''}
            </Button>
          )}
        </Space>
      }
    >
      {/* Step 1 — Upload */}
      <InvoiceFileUpload
        uploadedFile={uploadedFile}
        parsing={parsing}
        captureToken={captureToken}
        onFileSelect={onFileSelect}
        onRemove={onRemoveFile}
        onParse={onParse}
      />

      {parsing && (
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <SharedSpinner loading />
          <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: 13 }}>
            {L('Extracting data from invoice...')}
          </div>
        </div>
      )}

      {/* Step 2 — Supplier after parse */}
      {(parsedInvoice || uploadedFile) && !parsing && (
        <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
          <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
            {L('Supplier')}
          </Text>

          {parsedInvoice?.supplierName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {L('Detected')}: <strong>{parsedInvoice.supplierName}</strong>
              </Text>
              {attachedSupplier && attachedSupplier.id === supplierId ? (
                <Text style={{ fontSize: 12, color: '#52c41a' }}>✓ {L('Attached')}</Text>
              ) : (
                <Button size="small" type="link" icon={<LinkOutlined />} onClick={onAttachSupplier} style={{ padding: 0, height: 'auto' }}>
                  {L('Attach supplier')}
                </Button>
              )}
            </div>
          )}

          <SupplierSelect value={supplierId} onChange={onSupplierChange} />

          {parsedInvoice && !hasResults && (
            <Button
              type="primary"
              style={{ marginTop: 10, width: '100%' }}
              loading={matching}
              disabled={!supplierId}
              onClick={onMatch}
            >
              {L('Match to open purchase orders')}
            </Button>
          )}
        </div>
      )}

      {matching && (
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <SharedSpinner loading />
          <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: 13 }}>{L('Matching...')}</div>
        </div>
      )}

      {/* Step 3 — Verification table */}
      {hasResults && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <Text strong>{L('Verification')}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {matchedCount} / {matchResults.length} {L('matched')}
            </Text>
          </div>

          {matchResults.map((result, index) => (
            <POReceiveLineCard
              key={index}
              result={result}
              receivedQty={lineQtys[index] ?? result.suggestedReceivedQty ?? 1}
              costPrice={lineCosts[index]}
              selectedPOId={lineSelectedPO[index] ?? result.bestMatch?.id}
              onReceivedQtyChange={(val) => onLineQtyChange(index, val)}
              onCostPriceChange={(val) => onLineCostChange(index, val)}
              onSelectPO={(id) => onLineSelectPO(index, id)}
            />
          ))}

          {!canSubmit && supplierId && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {L('No matched lines to receive.')}
            </Text>
          )}
        </div>
      )}
    </Drawer>
  );
};
